import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import {
  RegPlate, StatusText, StatusPill, Skeleton, EmptyState,
  ListControls, useListPrefs, type SortOption,
} from '../components/ui';
import { api, type InspectionSummary } from '../lib/api';
import { useLoad } from '../lib/hooks';
import { formatDate, retentionCutoff } from '../../../shared/schedule';
import './screens.css';

type Filter = 'all' | 'open' | 'signed' | 'VEH001' | 'VEH002';
type Sort = 'newest' | 'oldest' | 'defects' | 'vehicle';

const SORTS: SortOption<Sort>[] = [
  { key: 'newest', label: 'Newest first', hint: 'Most recent inspection date' },
  { key: 'oldest', label: 'Oldest first' },
  { key: 'defects', label: 'Most defects' },
  { key: 'vehicle', label: 'Vehicle A-Z' },
];

const dateOf = (i: InspectionSummary) => i.inspection_date ?? '';
const idOf = (i: InspectionSummary) => (i.reg_no ?? i.fleet_no ?? '').toUpperCase();

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'In progress' },
  { key: 'signed', label: 'Completed' },
  { key: 'VEH001', label: 'Vehicles' },
  { key: 'VEH002', label: 'Trailers' },
];

export default function Inspections() {
  const [prefs, setPrefs, resetPrefs] = useListPrefs('records', {
    filter: 'all' as Filter,
    sort: 'newest' as Sort,
  });
  const { filter, sort } = prefs;
  const { data, loading, error, reload } = useLoad<{ inspections: InspectionSummary[] }>(() =>
    api.get('/inspections?limit=300'),
  );

  const inspections = useMemo(() => {
    let list = data?.inspections ?? [];
    switch (filter) {
      case 'open':
        list = list.filter((i) => i.status === 'draft' || i.status === 'defects');
        break;
      case 'signed':
        list = list.filter((i) => i.status === 'signed');
        break;
      case 'VEH001':
      case 'VEH002':
        list = list.filter((i) => i.form_code === filter);
        break;
    }

    const sorted = [...list];
    switch (sort) {
      case 'oldest':
        sorted.sort((a, b) => dateOf(a).localeCompare(dateOf(b)));
        break;
      case 'defects':
        sorted.sort((a, b) => b.defect_count - a.defect_count || dateOf(b).localeCompare(dateOf(a)));
        break;
      case 'vehicle':
        sorted.sort((a, b) => idOf(a).localeCompare(idOf(b)) || dateOf(b).localeCompare(dateOf(a)));
        break;
      default:
        sorted.sort((a, b) => dateOf(b).localeCompare(dateOf(a)));
    }
    return sorted;
  }, [data, filter, sort]);

  return (
    <Screen
      onRefresh={reload} title="Records" subtitle="Safety inspection history">
      <div className="stack-4">
        <ListControls<Filter, Sort>
          chips={FILTERS}
          chip={filter}
          onChip={(key) => setPrefs({ filter: key })}
          sortOptions={SORTS}
          sort={sort}
          onSort={(key) => setPrefs({ sort: key })}
          activeFilterCount={(filter !== 'all' ? 1 : 0) + (sort !== 'newest' ? 1 : 0)}
          onClearFilters={resetPrefs}
        />

        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && inspections.length === 0 ? (
          <EmptyState
            title="No records here"
            body="Completed inspections are kept for at least 15 months and appear in this list."
          />
        ) : null}

        {inspections.length > 0 && (
          <div className="panel divide">
            {inspections.map((insp) => (
              <Link
                key={insp.id}
                to={insp.locked_at ? `/inspections/${insp.id}` : `/inspections/${insp.id}/run`}
                className="listrow"
              >
                <div className="listrow-lead">
                  <RegPlate reg={insp.reg_no} fleetNo={insp.fleet_no} size="sm" />
                </div>
                <div className="grow">
                  <div className="listrow-title truncate">
                    {insp.inspection_date ? formatDate(insp.inspection_date) : 'Not dated'}
                    {insp.iso_week ? <span className="dim"> · Wk {insp.iso_week}</span> : null}
                  </div>
                  <div className="listrow-sub truncate">
                    <span className="mono tiny">{insp.ref}</span>
                    <span className="dot" />
                    {insp.customer_name ?? 'No customer'}
                    {insp.defect_count > 0 ? (
                      <>
                        <span className="dot" />
                        {insp.defect_count} defect{insp.defect_count === 1 ? '' : 's'}
                      </>
                    ) : null}
                  </div>
                </div>
                {insp.status === 'defects' ? (
                  <StatusPill state="defects" size="sm">
                    {label(insp)}
                  </StatusPill>
                ) : (
                  <StatusText state={insp.status}>{label(insp)}</StatusText>
                )}
              </Link>
            ))}
          </div>
        )}

        {data && data.inspections.length > 0 ? (
          <p className="tiny dim" style={{ textAlign: 'center' }}>
            Records on or after {formatDate(retentionCutoff())} must be retained (15 months).
          </p>
        ) : null}
      </div>
    </Screen>
  );
}

function label(insp: InspectionSummary) {
  if (insp.status === 'amended') return 'Superseded';
  if (insp.status === 'signed') return insp.overall_result === 'satisfactory' ? 'Pass' : 'Rectified';
  if (insp.status === 'defects') return insp.signed_at ? 'Rectifying' : 'Defects';
  return 'Draft';
}
